/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * ours
 */
export type ReducerType<State, Action> = (state: State, action: Action) => State;

export type HandlerType<State, Action> = {
  [name: string]: ReducerType<State, Action>;
};

/*
 * Everything that follows is thanks to redux toolkit
 */
export type Actions<T extends keyof any = string> = Record<T, Action>;
// eslint-disable-next-line @typescript-eslint/ban-types
export type NotFunction<T> = T extends Function ? never : T;
export type CaseReducer<S = unknown, A extends Action = AnyAction> = (
  state: S,
  action: A
) => S | void;

export type CaseReducers<S, AS extends Actions> = {
  [T in keyof AS]: AS[T] extends Action ? CaseReducer<S, AS[T]> : void
};

export type TypeGuard<T> = {
  (value: unknown): value is T;
};

export type ReadonlyActionMatcherDescriptionCollection<S> = ReadonlyArray<ActionMatcherDescription<S, any>>;

export type ActionMatcherDescriptionCollection<S> = ActionMatcherDescription<S, any>[];

export type ActionMatcherDescription<S, A extends AnyAction> = {
  matcher: TypeGuard<A>;
  reducer: CaseReducer<S, A>;
};

export type Reducer<S = any, A extends Action = AnyAction> = (
  state: S | undefined,
  action: A
) => S;

export type ReducerWithInitialState<S extends NotFunction<any>> = Reducer<S> & {
  getInitialState: () => S;
};

export interface Action<T = string, Payload = unknown> {
  type: T;
  payload: Payload;
};

export interface AnyAction extends Action {
  [extraProps: string]: unknown;
};

export const createReducer = <
  S extends NotFunction<unknown>,
  CR extends CaseReducers<S, any> = CaseReducers<S, any>
>(
  initialState: S,
  actionsMap: CR,
  actionMatchers: ReadonlyActionMatcherDescriptionCollection<S> = [],
): ReducerWithInitialState<S> => {
  const reducer = (state = initialState, action: any): S => {
    const caseReducers = [
      actionsMap[action.type],
      ...actionMatchers
        .filter(({ matcher }) => matcher(action))
        .map(({ reducer }) => reducer),
    ];

    return caseReducers.reduce((previousState, caseReducer): S => {
      if (caseReducer) {
        const result = caseReducer(state, action);

        return result ? result : previousState;
      }

      return previousState;
    }, state);
  };

  return reducer as ReducerWithInitialState<S>;
};

export const makeMapDispatch = <A extends AnyAction>(dispatch: React.Dispatch<A>) =>
  <T>(f: (payload?: T) => A) =>
    (payload?: T) => dispatch(f(payload));
